"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import type { Locale } from "@/lib/booking/translations";
import { BOOKING_REVIEWS, REVIEW_AGGREGATE } from "@/lib/booking/reviews";

const ROTATE_MS = 4000;
// Trustpilot's signature green — signals these are real, verified reviews.
const TP_GREEN = "#00b67a";

function Stars({ count, size = 14 }: { count: number; size?: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${count} av 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className="inline-flex items-center justify-center rounded-[3px]"
          style={{ background: i < count ? TP_GREEN : "#dcdce6", width: size + 4, height: size + 4 }}
        >
          <Star size={size} className="text-white" fill="white" strokeWidth={0} />
        </span>
      ))}
    </div>
  );
}

export default function BookingReviews({
  locale = "sv",
  className = "",
}: {
  locale?: Locale;
  className?: string;
}) {
  const reviews = BOOKING_REVIEWS.filter((r) => r.rating >= 4);
  const [index, setIndex] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const id = window.setInterval(() => {
      if (!paused.current) setIndex((i) => (i + 1) % reviews.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [reviews.length]);

  if (reviews.length === 0) return null;
  const review = reviews[index];
  const sv = locale === "sv";

  return (
    <div
      className={`rounded-2xl border border-gray-200/80 bg-gradient-to-br from-white to-gray-50/80 p-4 shadow-sm ${className}`}
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      {/* Aggregate badge */}
      <a
        href={REVIEW_AGGREGATE.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
      >
        <Star size={13} className="text-[#00b67a]" fill={TP_GREEN} strokeWidth={0} />
        <span className="font-bold text-gray-700">Trustpilot</span>
        <span aria-hidden>·</span>
        <span>
          {REVIEW_AGGREGATE.score.toLocaleString(sv ? "sv-SE" : "en-US")}{" "}
          {sv ? `· ${REVIEW_AGGREGATE.count}+ omdömen` : `· ${REVIEW_AGGREGATE.count}+ reviews`}
        </span>
      </a>

      {/* Rotating review — fixed height + absolutely-positioned figures so a
          longer review never resizes the card. */}
      <div className="relative mt-3 h-[124px]">
        <AnimatePresence mode="wait">
          <motion.figure
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <div className="flex items-center gap-2">
              <Stars count={review.rating} />
              <span className="text-[12px] font-semibold text-gray-700">{review.author}</span>
            </div>
            <blockquote className="mt-2 line-clamp-4 text-[13px] leading-relaxed text-gray-800">
              “{review.text[locale]}”
            </blockquote>
          </motion.figure>
        </AnimatePresence>
      </div>
    </div>
  );
}
